<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5" title="">
      <v-row align="center" class="mb-8" no-gutters>
        <v-col cols="2">
          <v-combobox class="mr-2" v-model="selected_discontinuation_interval" :items="select_discontinuation_interval_items"
                      label="Discontinuation interval"></v-combobox>
        </v-col>
        <v-col cols="2">
          <v-combobox class="mr-2" v-model="selected_maximum_treatment_phase" :items="select_maximum_treatment_phase_items"
                      label="Maximum number of treatment phases to display"></v-combobox>
        </v-col>
        <v-col cols="2">
          <v-combobox v-model="selected_zoom_level" :items="select_zoom_level_items"
                      label="Zoom level (reducing difference in line weights)"></v-combobox>
        </v-col>
        <v-col cols="2">
          <v-checkbox v-model="kaplan_meier_confidence_intervals" label="Show confidence intervals"
                      hide-details></v-checkbox>
        </v-col>
        <v-col cols="2">
        </v-col>
        <v-col cols="1">
          <v-btn class="ml-2 mt-2" color="secondary" @click.stop="this.sanKeyMoveUpInZoom"
                 :disabled="this.selected_previous_phase.length === 0">Move up</v-btn>
        </v-col>
        <v-col cols="1">
          <v-btn class="ml-2 mt-2" color="secondary" @click.stop="this.sanKeyResetZoom"
                 :disabled="this.selected_previous_phase.length === 0">Reset</v-btn>
        </v-col>
      </v-row>
      <v-row class="mb-8" align="center" no-gutters>
        <v-col cols="12">
          <GChart :settings="{ packages: ['sankey'] }" type="Sankey"
                  :data="sanKeyChartData"
                  :options="sanKeyChartOptions"
                  @ready="sanKeyChartReady"
                  v-if="sanKeyChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
      <v-row :no-gutters="true">
        <v-col cols="12">
          <v-list>
            <v-list-item prepend-icon="mdi:mdi-information">
              The radiotherapy, surgery, and chemotherapy is the occurrence of the <b>first</b> such treatment.
              Individuals could have had additional radiotherapy, surgery, or chemotherapy after this specific date.
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-expansion-panels :multiple="true">
          <v-expansion-panel title="Healthcare resource utilization inpatient care"
                             text="Follow-up year 0 is the same calendar year as diagnosis. Total number of patients in care is shown as N below each follow-up year."
                             @group:selected="(event) => this.revealHealthcareResourceUtilizationInPatientChart = event.value">
            <v-expand-transition mode="out-in">
              <v-card elevation="0"
                      v-if="((healthcareResourceUtilizationInPatientChartData.length !== 0)
                          || (healthcareResourceUtilizationOutPatientChartData.length !== 0))
                          && revealHealthcareResourceUtilizationInPatientChart">
                <v-row align="center" no-gutters>
                  <v-col>
                    <GChart type="ColumnChart"
                            :data="healthcareResourceUtilizationInPatientChartData"
                            :options="healthcareResourceUtilizationInPatientChartOptions"
                            v-if="healthcareResourceUtilizationInPatientChartData.length !== 0"/>
                    <v-chip variant="elevated" size="x-large" density="default" v-else>
                      No data to display. Please adjust your filters.
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card>
            </v-expand-transition>
          </v-expansion-panel>
          <v-expansion-panel title="Healthcare resource utilization outpatient care"
                             text="Follow-up year 0 is the same calendar year as diagnosis. Total number of patients in care is shown as N below each follow-up year."
                             @group:selected="(event) => this.revealHealthcareResourceUtilizationOutPatientChart = event.value">
            <v-expand-transition mode="out-in">
              <v-card elevation="0"
                      v-if="((healthcareResourceUtilizationInPatientChartData.length !== 0)
                          || (healthcareResourceUtilizationOutPatientChartData.length !== 0))
                          && revealHealthcareResourceUtilizationOutPatientChart">
                <v-row align="center" no-gutters>
                  <v-col>
                    <GChart type="ColumnChart"
                            :data="healthcareResourceUtilizationOutPatientChartData"
                            :options="healthcareResourceUtilizationOutPatientChartOptions"
                            v-if="healthcareResourceUtilizationOutPatientChartData.length !== 0"/>
                    <v-chip variant="elevated" size="x-large" density="default" v-else>
                      No data to display. Please adjust your filters.
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card>
            </v-expand-transition>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
      <v-row no-gutters>
        <v-col cols="6">
          <v-card class="mx-auto" min-height="800" color="primary" variant="plain" elevation="0"
                  :title="timeToTreatmentChartTitle">
            <GChart type="SteppedAreaChart" :data="timeToTreatmentChartData"
                    :options="timeToTreatmentChartOptions"
                    v-if="timeToTreatmentChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please select a treatment path.
            </v-chip>
            <v-sheet class="pa-2 ma-2" v-for="(number, index) in timeToTreatmentNumbers" :key="index">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="number">
                    <span class="large-number">{{ number.value }}</span>
                    <span class="confidence-interval" v-if="number.confidenceInterval">&nbsp;{{ number.confidenceInterval }}</span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ number.label }}</div>
              </div>
            </v-sheet>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="mx-auto" min-height="800" color="primary" variant="plain" elevation="0"
                  :title="discontinuationTimeChartTitle">
            <GChart type="SteppedAreaChart" :data="discontinuationTimeChartData"
                    :options="discontinuationTimeChartOptions"
                    v-if="discontinuationTimeChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Note that discontinuation is only displayed for ATC codes.
            </v-chip>
            <v-sheet class="pa-2 ma-2" v-for="(number, index) in discontinuationTimeNumbers" :key="index">
              <div class="number-box">
                <transition name="fade-bounce" mode="out-in">
                  <div :key="number">
                    <span class="large-number">{{ number.value }}</span>
                    <span class="confidence-interval" v-if="number.confidenceInterval">&nbsp;{{ number.confidenceInterval }}</span>
                  </div>
                </transition>
                <div class="label large-number-label">{{ number.label }}</div>
              </div>
            </v-sheet>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";

const SANKEY_STATE_READY = 0;
const SANKEY_STATE_POPPED = 1;

export default defineComponent({
  name: 'CancerTreatmentPaths',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    metastasisStatusM: Array,
    metastasisStatusN: Array,
    atcCodes: Array,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    select_discontinuation_interval_items: [
      {
        title: 'One month',
        value: 1
      },
      {
        title: 'Two months',
        value: 2
      },
      {
        title: 'Three months',
        value: 3
      },
      {
        title: 'Four months',
        value: 4
      },
      {
        title: 'Five months',
        value: 5
      },
      {
        title: 'Six months',
        value: 6
      }
    ],
    selected_discontinuation_interval: {
      title: 'Three months',
      value: 3
    },
    select_maximum_treatment_phase_items: [
      {
        title: 'Two',
        value: 2
      },
      {
        title: 'Three',
        value: 3
      },
      {
        title: 'Four',
        value: 4
      },
      {
        title: 'Five',
        value: 5
      },
      {
        title: 'Six',
        value: 6
      },
      {
        title: 'Seven',
        value: 7
      },
      {
        title: 'Eight',
        value: 8
      },
      {
        title: 'Nine',
        value: 9
      },
      {
        title: 'Ten',
        value: 10
      },
      {
        title: 'Eleven',
        value: 11
      },
      {
        title: 'Twelve',
        value: 12
      },
      {
        title: 'Thirteen',
        value: 13
      },
      {
        title: 'Fourteen',
        value: 14
      },
      {
        title: 'Fifteen',
        value: 15
      },
      {
        title: 'Sixteen',
        value: 16
      },
      {
        title: 'Seventeen',
        value: 17
      },
      {
        title: 'Eighteen',
        value: 18
      },
      {
        title: 'Nineteen',
        value: 19
      },
      {
        title: 'Twenty',
        value: 20
      }
    ],
    selected_zoom_level: {
      title: '1',
      value: 8
    },
    select_zoom_level_items: [
      {
        title: '1',
        value: 8
      },
      {
        title: '2',
        value: 4
      },
      {
        title: '3',
        value: 2
      },
      {
        title: '4',
        value: 1
      }
    ],
    selected_maximum_treatment_phase: {
      title: 'Four',
      value: 4
    },
    kaplan_meier_confidence_intervals: false,
    selected_phase: {},
    selected_previous_phase: [],
    selected_previous_sankey: [],
    sankey_state: SANKEY_STATE_READY,
    sanKeyChartData: [],
    sanKeyTransitionData: [],
    sanKeyChartOptions: {
      height: 800,
      sankey: {
        node: {
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          label: {
            fontSize: 14,
            color: '#ffffff',
            bold: true
          }
        },
        link: {
          colorMode: 'gradient',
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          interactivity: true
        }
      },
      tooltip: {
        isHtml: true
      }
    },
    timeToTreatmentNumbers: [],
    timeToTreatmentChartTitle: '',
    timeToTreatmentChartData: [],
    timeToTreatmentChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      areaOpacity: 0,
      height: 800,
      colors: ['#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7', '#a6cee3'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: 'Time (months)',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Probability',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.5
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    discontinuationTimeNumbers: [],
    discontinuationTimeChartTitle: '',
    discontinuationTimeChartData: [],
    discontinuationTimeChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      areaOpacity: 0,
      height: 800,
      colors: ['#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7', '#a6cee3'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: 'none',
      hAxis: {
        title: 'Time (months)',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Probability',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.5
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: {
        'style': 'boxes',
        'boxWidth': 1,
        'lineWidth': 0,
        'fillOpacity': 0.2
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    revealHealthcareResourceUtilizationInPatientChart: false,
    healthcareResourceUtilizationInPatientChartData: [],
    healthcareResourceUtilizationInPatientChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'In-patient care',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      legend: {
        position: 'top',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: false,
      hAxis: {
        title: 'Follow-up year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: 'Number of patients',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        logScale: false
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    revealHealthcareResourceUtilizationOutPatientChart: false,
    healthcareResourceUtilizationOutPatientChartData: [],
    healthcareResourceUtilizationOutPatientChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'Out-patient care',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      legend: {
        position: 'top',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: false,
      hAxis: {
        title: 'Follow-up year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: 'Number of patients',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        logScale: false
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  watch: {
    selected_phase() {
      this.loadDynamicData()
    },
    selected_discontinuation_interval() {
      this.loadDynamicData()
    },
    selected_maximum_treatment_phase() {
      this.loadDynamicData()
    },
    selected_zoom_level() {
      this.loadDynamicData()
    },
    kaplan_meier_confidence_intervals() {
      this.loadDynamicData()
    }
  },
  methods: {
    async loadSanKeyChartData() {
      let postData = {
        icd: this.icd,
        age_at_diagnosis: this.ageAtDiagnosis.join(","),
        year_of_diagnosis: this.diagnosisDate.join(","),
        survival_time_in_years: this.survivalTimeInYears.join(","),
        metastasis_status_m: this.metastasisStatusM.join(","),
        metastasis_status_n: this.metastasisStatusN.join(","),
        atc_codes: this.atcCodes.join(","),
        discontinuation_interval: this.selected_discontinuation_interval.value,
        pre_phase: this.selected_phase.prePhase,
        post_phase: this.selected_phase.postPhase,
        phase: this.selected_phase.phase,
        maximum_treatment_phase: this.selected_maximum_treatment_phase.value,
        zoom_level: this.selected_zoom_level.value
      }
      return axios.post('cancer-sankey-data',
          postData, {
            headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`}
          });
    },
    async loadTimeToTreatmentChartData() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        this.timeToTreatmentChartTitle = 'Time to treatment'
        return {'data': []}
      }

      let data = axios.get('cancer-treatment-paths-time-to-treatment', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
          age_at_diagnosis: this.ageAtDiagnosis.join(","),
          year_of_diagnosis: this.diagnosisDate.join(","),
          survival_time_in_years: this.survivalTimeInYears.join(","),
          metastasis_status_m: this.metastasisStatusM.join(","),
          metastasis_status_n: this.metastasisStatusN.join(","),
          atc_codes: this.atcCodes.join(","),
          discontinuation_interval: this.selected_discontinuation_interval.value,
          pre_phase: prePhase,
          post_phase: postPhase,
          phase: phase,
          kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
        }
      });
      return data.then((response) => {
        if ((prePhase !== 'Death') && (prePhase !== 'End of follow-up')) {
          if ((prePhase !== 'Diagnosis') && (postPhase !== 'Death') && (postPhase !== 'End of follow-up')) {
            this.timeToTreatmentChartTitle = 'Time from ' + (phase - 1) + '. ' + prePhase + ' to ' + phase + '. ' + postPhase
          }
          else if ((prePhase === 'Diagnosis') && (postPhase !== 'Death')  && (postPhase !== 'End of follow-up')) {
            this.timeToTreatmentChartTitle = 'Time from Diagnosis to ' + phase + '. ' + postPhase
          }
          else if ((prePhase !== 'Diagnosis') && (postPhase === 'Death')) {
            this.timeToTreatmentChartTitle = 'Time from ' + (phase - 1) + '. ' + prePhase + ' to Death'
          }
          else if ((prePhase !== 'Diagnosis') && (postPhase === 'End of follow-up')) {
            this.timeToTreatmentChartTitle = 'Time from ' + (phase - 1) + '. ' + prePhase + ' to End of follow-up'
          }
        }
        return response;
      });
    },
    async loadTimeToTreatmentNumbers() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        return {'data': []}
      }

      return axios.get('cancer-treatment-paths-time-to-treatment-numbers', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
          age_at_diagnosis: this.ageAtDiagnosis.join(","),
          year_of_diagnosis: this.diagnosisDate.join(","),
          survival_time_in_years: this.survivalTimeInYears.join(","),
          metastasis_status_m: this.metastasisStatusM.join(","),
          metastasis_status_n: this.metastasisStatusN.join(","),
          atc_codes: this.atcCodes.join(","),
          discontinuation_interval: this.selected_discontinuation_interval.value,
          pre_phase: prePhase,
          post_phase: postPhase,
          phase: phase,
          kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
        }
      });
    },
    async loadDiscontinuationTimeChartData() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        this.discontinuationTimeChartTitle = 'Discontinuation'
        return {'data': []}
      }
      let data = axios.get('cancer-treatment-paths-discontinuation-time', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
          age_at_diagnosis: this.ageAtDiagnosis.join(","),
          year_of_diagnosis: this.diagnosisDate.join(","),
          survival_time_in_years: this.survivalTimeInYears.join(","),
          metastasis_status_m: this.metastasisStatusM.join(","),
          metastasis_status_n: this.metastasisStatusN.join(","),
          atc_codes: this.atcCodes.join(","),
          discontinuation_interval: this.selected_discontinuation_interval.value,
          pre_phase: prePhase,
          post_phase: postPhase,
          phase: phase,
          kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
        }
      });
      return data.then((response) => {
        if ((prePhase !== 'Death') && (prePhase !== 'End of follow-up') && (postPhase !== 'Death')
            && (postPhase !== 'End of follow-up')) {
          this.discontinuationTimeChartTitle = 'Discontinuation of ' + phase + '. ' + postPhase
        }
        return response;
      });
    },
    async loadDiscontinuationTimeNumbers() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        return {'data': []}
      }

      return axios.get('cancer-treatment-paths-discontinuation-time-numbers', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
          age_at_diagnosis: this.ageAtDiagnosis.join(","),
          year_of_diagnosis: this.diagnosisDate.join(","),
          survival_time_in_years: this.survivalTimeInYears.join(","),
          metastasis_status_m: this.metastasisStatusM.join(","),
          metastasis_status_n: this.metastasisStatusN.join(","),
          atc_codes: this.atcCodes.join(","),
          discontinuation_interval: this.selected_discontinuation_interval.value,
          pre_phase: prePhase,
          post_phase: postPhase,
          phase: phase,
          kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
        }
      });
    },
    async loadHealthcareResourceUtilizationInPatientData() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        return {'data': []}
      }

      return axios.get('cancer-patients-healthcare-in-patient-resource-utilization-patient-ids',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(","),
              discontinuation_interval: this.selected_discontinuation_interval.value,
              pre_phase: prePhase,
              post_phase: postPhase,
              phase: phase
            }
          });
    },
    async loadHealthcareResourceUtilizationOutPatientData() {
      let prePhase = this.selected_phase.prePhase
      let postPhase = this.selected_phase.postPhase
      let phase = this.selected_phase.phase

      if (!prePhase || !postPhase || !phase) {
        return {'data': []}
      }

      return axios.get('cancer-patients-healthcare-out-patient-resource-utilization-patient-ids',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(","),
              discontinuation_interval: this.selected_discontinuation_interval.value,
              pre_phase: prePhase,
              post_phase: postPhase,
              phase: phase
            }
          });
    },
    async sanKeyChartReady(chart, google) {
      const setSanKeyData = (selectedRow, selectPrevious) => {
        let selected_phase = this.sanKeyTransitionData[selectedRow]
        if ((selected_phase.prePhase !== 'Death') && (selected_phase.prePhase !== 'End of follow-up')
            && (selected_phase.postPhase !== 'End of follow-up')) {
          this.selected_previous_phase.push(this.selected_phase)
          this.selected_previous_sankey.push(selectPrevious)
          this.selected_phase = selected_phase
        }
      }
      const isSankeyStateReady = () => this.sankey_state === SANKEY_STATE_READY
      const setSankeyStateReady = () => this.sankey_state = SANKEY_STATE_READY
      const selectHandler = function () {
        const selection = chart.getSelection()
        const selectedItem = selection[0];
        if (selectedItem && isSankeyStateReady()) {
          setSanKeyData(selectedItem.row, selection)
        }
        setSankeyStateReady()
      }

      const popPreviousSankey = () => {
        if (this.selected_previous_sankey.length === this.selected_previous_phase.length + 1) {
          return this.selected_previous_sankey.pop()
        }
      }
      const setSankeyStatePopped = () => this.sankey_state = SANKEY_STATE_POPPED
      const readyHandler = function () {
        let previousSankey = popPreviousSankey()
        if (previousSankey) {
          setSankeyStatePopped()
          chart.setSelection(previousSankey)
        }
      }

      google.visualization.events.addListener(chart, 'ready', readyHandler)
      google.visualization.events.addListener(chart, 'select', selectHandler);
    },
    async sanKeyMoveUpInZoom() {
      if (this.selected_previous_phase.length > 0) {
        this.selected_phase = this.selected_previous_phase.pop()
      }
    },
    async sanKeyResetZoom() {
      this.sankey_state = SANKEY_STATE_READY
      this.selected_phase = {}
      this.selected_previous_phase=[]
      this.selected_previous_sankey=[]
      this.timeToTreatmentChartData = []
      this.discontinuationTimeChartData = []
      this.timeToTreatmentChartTitle = 'Time to treatment'
      this.discontinuationTimeChartTitle = 'Discontinuation'
      this.timeToTreatmentNumbers = []
      this.discontinuationTimeNumbers = []
    },
    async loadDynamicData() {
      await Promise.all([
        this.loadSanKeyChartData(),
        this.loadTimeToTreatmentChartData(),
        this.loadTimeToTreatmentNumbers(),
        this.loadDiscontinuationTimeChartData(),
        this.loadDiscontinuationTimeNumbers(),
        this.loadHealthcareResourceUtilizationInPatientData(),
        this.loadHealthcareResourceUtilizationOutPatientData()
      ]).then(responses => {
        this.sanKeyChartData = responses[0].data.graphData;
        this.sanKeyTransitionData = responses[0].data.transitionData;
        this.timeToTreatmentChartData = responses[1].data;
        this.timeToTreatmentNumbers = responses[2].data;
        this.discontinuationTimeChartData = responses[3].data;
        this.discontinuationTimeNumbers = responses[4].data;
        this.healthcareResourceUtilizationInPatientChartData = responses[5].data;
        this.healthcareResourceUtilizationOutPatientChartData = responses[6].data;
        console.log('Dynamic data loaded: CancerTreatmentPaths')
      })
    }
  },
  mounted() {
    this.loadDynamicData()
  },
  updated() {
    if (this.visible) {
      this.loadDynamicData()
    }
  }
});
</script>

<style scoped>
.number-box {
  justify-content: space-around;
  text-align: center;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.confidence-interval {
  font-size: 1em;
  color: #a6cee3;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.fade-bounce-enter-active {
  transition: all 0.5s ease-in;
  animation: bounce-in 0.5s;
}
.fade-bounce-leave-active {
  transition: all 0.5s ease-out;
  animation: bounce-in 0.5s reverse;
}

.fade-bounce-enter-from,
.fade-bounce-leave-to {
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}
</style>
